import {
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from 'react-query';
import {
  createNewTag,
  CreateNewTagArgs,
  deleteTag,
  DeleteTagArgs,
  DeleteTagResponse,
  getTags,
  updateTags,
  UpdateTagsArgs,
  UpdateTagsResponse,
} from './actions';
import { Tag } from '~/types/models/tags';
import type { DefaultGetResponse } from '~/App/Api/rest';
import { AxiosError } from 'axios';
import {
  tagResource,
  TagResourceArgs,
  untagResource,
  UntagResourceArgs,
} from '~/App/Modules/Testimonials/Actions/respondentsAction';
import { TagTestimonialResponseResponse } from '~/types/api/testimonials';

export const queryKeys = {
  all: ['tags'],
  lists: () => [...queryKeys.all, 'list'] as const,
};
export interface TagHookParams {
  options?: UseQueryOptions<DefaultGetResponse<Tag[]>, AxiosError>;
}

export const useTags = (params?: TagHookParams) => {
  return useQuery<DefaultGetResponse<Tag[]>, AxiosError>(
    queryKeys.lists(),
    getTags,
    {
      refetchOnWindowFocus: false,
      ...params?.options,
    }
  );
};

/**
 * Add the tag to current tags list.
 */
export const useCreateTag = (
  options?: UseMutationOptions<Tag, AxiosError, CreateNewTagArgs>
) => {
  const queryClient = useQueryClient();
  return useMutation(createNewTag, {
    onSuccess: () => {
      queryClient.invalidateQueries(queryKeys.lists());
    },
    ...options,
  });
};

/**
 * Update the tags list with the tags thas has an update (name changed., etc)
 */
export const useUpdateTags = (
  options?: UseMutationOptions<UpdateTagsResponse, AxiosError, UpdateTagsArgs>
) => {
  const queryClient = useQueryClient();
  return useMutation(updateTags, {
    onSuccess: () => {
      queryClient.invalidateQueries(queryKeys.lists());
    },
    ...options,
  });
};

/**
 * Delete the tag from the tags list
 */
export const useDeleteTag = (
  options?: UseMutationOptions<DeleteTagResponse, AxiosError, DeleteTagArgs>
) => {
  const queryClient = useQueryClient();
  return useMutation(deleteTag, {
    onSuccess: () => {
      queryClient.invalidateQueries(queryKeys.lists());
    },
    ...options,
  });
};

/**
 * Add a tag to a resource
 */
export const useTagResource = (
  options?: UseMutationOptions<
    TagTestimonialResponseResponse,
    AxiosError,
    TagResourceArgs
  >
) => useMutation(tagResource, options);

/**
 * Remove a tag from a resource
 */
export const useUntagResource = (
  options?: UseMutationOptions<
    TagTestimonialResponseResponse,
    AxiosError,
    UntagResourceArgs
  >
) => useMutation(untagResource, options);
